import { THREE } from '../../../lib/three-client.js';
import { getSlopeColorRgb } from './slopeThreeColors.js';

/**
 * Veri {x,y,z} -> Three.js [x, heightScale*(z-minZ), -y]
 */
export function toThreePos(v, minZ, heightScale) {
  const x = Number(v.x) || 0;
  const y = Number(v.y) || 0;
  const z = Number(v.z) || 0;
  return [x, heightScale * (z - minZ), -y];
}

export function createParcelMesh(data) {
  const mesh = data?.mesh;
  const vertices = mesh?.vertices || [];
  const triangles = mesh?.triangles || [];
  if (vertices.length < 3 || triangles.length < 1) return null;

  const elevs = vertices.map((v) => Number(v.z)).filter(Number.isFinite);
  const minZ = elevs.length ? Math.min(...elevs) : 0;
  const heightScale = Number(mesh.height_scale) > 0 ? Number(mesh.height_scale) : 1.5;

  const positions = new Float32Array(vertices.length * 3);
  const colors = new Float32Array(vertices.length * 3);
  for (let i = 0; i < vertices.length; i += 1) {
    const [px, py, pz] = toThreePos(vertices[i], minZ, heightScale);
    positions[i * 3] = px;
    positions[i * 3 + 1] = py;
    positions[i * 3 + 2] = pz;
    const [r, g, b] = getSlopeColorRgb(vertices[i].slope);
    colors[i * 3] = r / 255;
    colors[i * 3 + 1] = g / 255;
    colors[i * 3 + 2] = b / 255;
  }

  const indices = [];
  for (const tri of triangles) {
    if (!Array.isArray(tri) || tri.length < 3) continue;
    indices.push(tri[0], tri[1], tri[2]);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    flatShading: false,
  });

  const parcelMesh = new THREE.Mesh(geometry, material);
  parcelMesh.name = 'parcelSurface';
  parcelMesh.userData = { minZ, heightScale, vertices };
  return parcelMesh;
}

export function createParcelOutline(data, parcelMesh) {
  const mesh = data?.mesh;
  const vertices = mesh?.vertices || [];
  const outline = mesh?.outline || [];
  if (!parcelMesh || outline.length < 3) return null;

  const { minZ, heightScale } = parcelMesh.userData;
  const eps = 0.08;
  const pts = [];
  for (const id of outline) {
    const v = vertices[id];
    if (!v) continue;
    const [x, y, z] = toThreePos(v, minZ, heightScale);
    pts.push(new THREE.Vector3(x, y + eps, z));
  }
  if (pts.length < 3) return null;
  pts.push(pts[0].clone());

  const geometry = new THREE.BufferGeometry().setFromPoints(pts);
  const material = new THREE.LineBasicMaterial({ color: 0x0f172a, linewidth: 2 });
  const line = new THREE.Line(geometry, material);
  line.name = 'parcelOutline';
  return line;
}

export function createSideWalls(data, parcelMesh) {
  const mesh = data?.mesh;
  const vertices = mesh?.vertices || [];
  const outline = mesh?.outline || [];
  if (!parcelMesh || outline.length < 3) return null;

  const wallDepth = Number(mesh.wall_depth);
  if (!Number.isFinite(wallDepth) || wallDepth <= 0) return null;

  const { minZ, heightScale } = parcelMesh.userData;
  const bottomY = -wallDepth;
  const positions = [];
  const n = outline.length;

  for (let i = 0; i < n; i += 1) {
    const a = vertices[outline[i]];
    const b = vertices[outline[(i + 1) % n]];
    if (!a || !b) continue;
    const [ax, ay, az] = toThreePos(a, minZ, heightScale);
    const [bx, by, bz] = toThreePos(b, minZ, heightScale);
    // iki ucgen: a_top, b_top, b_bot / a_top, b_bot, a_bot
    positions.push(
      ax, ay, az,
      bx, by, bz,
      bx, bottomY, bz,
      ax, ay, az,
      bx, bottomY, bz,
      ax, bottomY, az,
    );
  }

  if (!positions.length) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  const material = new THREE.MeshLambertMaterial({
    color: 0x94a3b8,
    side: THREE.DoubleSide,
  });
  const walls = new THREE.Mesh(geometry, material);
  walls.name = 'parcelWalls';
  return walls;
}

export function createRoadFrontage(data, parcelMesh) {
  const road = data?.road_frontage;
  const points = road?.points || [];
  if (!parcelMesh || points.length < 2) return null;

  const { minZ, heightScale } = parcelMesh.userData;
  const eps = 0.12;
  const pts = points.map((p) => {
    const [x, y, z] = toThreePos(p, minZ, heightScale);
    return new THREE.Vector3(x, y + eps, z);
  });
  const geometry = new THREE.BufferGeometry().setFromPoints(pts);
  const material = new THREE.LineBasicMaterial({ color: 0x2563eb, linewidth: 3 });
  const line = new THREE.Line(geometry, material);
  line.name = 'roadFrontage';
  line.userData = { length_m: road.length_m, mid: pts[Math.floor(pts.length / 2)] };
  return line;
}

/** YG hattı: iki direk + aradaki sarı hat (harita #FFEA00). */
export function createElectricLine(data, parcelMesh) {
  const electric = data?.electric_line;
  const poles = electric?.poles || electric?.points || [];
  if (!parcelMesh || poles.length < 2) return null;

  const { minZ, heightScale } = parcelMesh.userData;
  const p0 = poles[0];
  const p1 = poles[poles.length - 1];
  const base0 = toThreePos(p0, minZ, heightScale);
  const base1 = toThreePos(p1, minZ, heightScale);

  const poleHeight = 7.5;
  const shaftRadius = 0.28;
  const crossHalf = 1.6;
  const color = 0xffea00;
  const shaftColor = 0x334155;

  const group = new THREE.Group();
  group.name = 'electricLine';
  group.renderOrder = 5;

  const makePole = (base) => {
    const [bx, by, bz] = base;
    const poleGroup = new THREE.Group();

    const shaftGeo = new THREE.CylinderGeometry(shaftRadius, shaftRadius * 1.15, poleHeight, 8);
    const shaftMat = new THREE.MeshLambertMaterial({ color: shaftColor });
    const shaft = new THREE.Mesh(shaftGeo, shaftMat);
    shaft.position.set(bx, by + poleHeight / 2, bz);
    poleGroup.add(shaft);

    const crossGeo = new THREE.BoxGeometry(crossHalf * 2, 0.22, 0.22);
    const crossMat = new THREE.MeshLambertMaterial({ color: shaftColor });
    const cross = new THREE.Mesh(crossGeo, crossMat);
    cross.position.set(bx, by + poleHeight * 0.92, bz);
    poleGroup.add(cross);

    return {
      group: poleGroup,
      tip: new THREE.Vector3(bx, by + poleHeight * 0.92, bz),
    };
  };

  const poleA = makePole(base0);
  const poleB = makePole(base1);
  group.add(poleA.group);
  group.add(poleB.group);

  const mid = new THREE.Vector3().addVectors(poleA.tip, poleB.tip).multiplyScalar(0.5);
  // Hafif salınım (catenary hissi)
  const sag = mid.clone();
  sag.y -= Math.min(1.8, poleHeight * 0.12);

  const curve = new THREE.CatmullRomCurve3([poleA.tip, sag, poleB.tip]);
  const tubeGeo = new THREE.TubeGeometry(curve, 20, 0.14, 6, false);
  const tubeMat = new THREE.MeshBasicMaterial({ color, depthWrite: false });
  const tube = new THREE.Mesh(tubeGeo, tubeMat);
  tube.name = 'electricWire';
  tube.renderOrder = 6;
  group.add(tube);

  group.userData = {
    kw: electric?.kw || null,
    area: electric?.area || null,
    mid: sag.clone(),
  };
  return group;
}

export function createSectionLines(data, parcelMesh) {
  if (!parcelMesh) return null;
  const { minZ, heightScale } = parcelMesh.userData;
  const group = new THREE.Group();
  group.name = 'sectionLines';
  const eps = 0.1;

  for (const key of ['a', 'b']) {
    const section = data?.sections?.[key];
    const points = section?.points || [];
    if (points.length < 2) continue;
    const pts = points.map((p) => {
      const [x, y, z] = toThreePos(p, minZ, heightScale);
      return new THREE.Vector3(x, y + eps, z);
    });
    const geometry = new THREE.BufferGeometry().setFromPoints(pts);
    const material = new THREE.LineDashedMaterial({
      color: key === 'a' ? 0x475569 : 0x94a3b8,
      dashSize: 2.2,
      gapSize: 1.4,
    });
    const line = new THREE.Line(geometry, material);
    line.computeLineDistances();
    line.name = `section-${key}`;
    group.add(line);
  }
  return group.children.length ? group : null;
}

export function fitCameraToObject(camera, object, controls, size) {
  if (!camera || !object) return;
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const center = box.getCenter(new THREE.Vector3());
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(sphere.radius, 1);

  // Ön yüz: üst yüzey kameraya dönük. Three z = -localY olduğu için
  // kamera +X / +Y / -Z tarafından bakınca SVG kart ile aynı taraf görünür.
  const dist = radius * 2.2;
  camera.position.set(
    center.x + dist * 0.55,
    center.y + dist * 0.62,
    center.z - dist * 0.72,
  );
  camera.up.set(0, 1, 0);
  camera.lookAt(center);
  if (controls) {
    controls.target.copy(center);
    controls.update();
  }

  const aspect = size.width / Math.max(size.height, 1);
  const pad = 1.25;
  const halfH = radius * pad;
  const halfW = halfH * aspect;
  camera.left = -halfW;
  camera.right = halfW;
  camera.top = halfH;
  camera.bottom = -halfH;
  camera.near = -radius * 20;
  camera.far = radius * 20;
  camera.zoom = 1;
  camera.updateProjectionMatrix();
}

export function projectWorldToScreen(renderer, camera, worldPos, container) {
  const v = worldPos.clone().project(camera);
  const rect = container.getBoundingClientRect();
  return {
    x: ((v.x + 1) / 2) * rect.width,
    y: ((-v.y + 1) / 2) * rect.height,
  };
}
